import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { OtcTradeApi } from "../OtcTrade/otcTradeApi";
import { isClosedRequest, receiveLine, requestWhen, stageLabel, statusTone } from "../OtcTrade/tradeStatus";
import OrderTablePagination, { ORDER_TABLE_PAGE_SIZE, useOrderTablePagination } from "./OrderTablePagination";
import "../OtcDesk/otc-desk.css";
import "../OtcTrade/otc-trade.css";

const TABS = [
  { id: "active", label: "Active requests", to: "/user_profile/orders/otc_orders" },
  { id: "history", label: "History", to: "/user_profile/orders/otc_order_history" },
];

function TradeDetail({ row, onClose }) {
  return createPortal(
    <div className="otc-trade-overlay" onClick={onClose}>
      <div className="otc-trade-popup" role="dialog" aria-modal="true" aria-labelledby="so-otc-detail-title" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="otc-trade-close" aria-label="Close" onClick={onClose}>×</button>
        <h2 id="so-otc-detail-title">{row.number}</h2>
        <p className="otc-trade-lead">{requestWhen(row.createdAt || row.updatedAt)}</p>
        <dl>
          <dt>Pair</dt><dd>{row.pairSymbol}</dd>
          <dt>Type</dt><dd>{row.category === "fiat" ? "Fiat spot" : "Crypto"}</dd>
          <dt>You pay</dt><dd>{row.payAmount} {row.payAsset}</dd>
          <dt>You get</dt><dd>{row.getAmount} {row.getAsset}</dd>
          <dt>Status</dt><dd><span className={`otc-status-chip ${statusTone(row.status)}`}>{stageLabel(row.status, row)}</span></dd>
          <dt>Receive</dt><dd>{receiveLine(row)}</dd>
          <dt>Purpose</dt><dd>{(row.dd && row.dd.purpose) || (row.clientDetails && row.clientDetails.purpose) || "—"}</dd>
          {row.dd && row.dd.direction === "crypto_to_fiat" ? (
            <>
              <dt>Currency</dt><dd>{row.dd.currency || "AED"}</dd>
              <dt>Demand draft amount</dt><dd>{row.dd.amount || row.getAmount} {row.dd.currency || row.getAsset}</dd>
              <dt>Amount in words</dt><dd>{row.dd.amountInWords || "—"}</dd>
              <dt>Collection</dt><dd>Office Collection</dd>
            </>
          ) : null}
          {row.status === "REJECTED" ? <><dt>Reason</dt><dd>{row.rejectReason || "—"}</dd></> : null}
        </dl>
      </div>
    </div>,
    document.body
  );
}

const OtcOrders = ({ initialTab = "active" }) => {
  const navigate = useNavigate();
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(initialTab === "history" ? "history" : "active");
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    OtcTradeApi.requests().then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (!res || res.success === false) {
        setAllRows([]);
        return;
      }
      setAllRows(Array.isArray(res.data) ? res.data : []);
    }).catch(() => {
      if (cancelled) return;
      setLoading(false);
      setAllRows([]);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    if (tab === "history") return allRows.filter((row) => isClosedRequest(row.status));
    return allRows.filter((row) => !isClosedRequest(row.status));
  }, [allRows, tab]);

  const { page, setPage, totalPages, pagedRows } = useOrderTablePagination(filtered, ORDER_TABLE_PAGE_SIZE);

  useEffect(() => {
    setTab(initialTab === "history" ? "history" : "active");
    setPage(1);
    setSelectedId("");
  }, [initialTab, setPage]);
  const selected = filtered.find((row) => (row._id || row.id || row.number) === selectedId)
    || allRows.find((row) => (row._id || row.id || row.number) === selectedId)
    || null;

  const empty = (
    <div className="so-emptyState" role="status">
      <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
    </div>
  );

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-otc-title">
            <h1 id="so-otc-title" className="so-page__title">
              {tab === "history" ? "OTC History" : "OTC Active"}
            </h1>
            <div className="so-subtabsWrap" role="tablist" aria-label="OTC orders">
              <div className="so-subtabs">
                {TABS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === item.id}
                    className={`so-subtabs__tab${tab === item.id ? " is-active" : ""}`}
                    onClick={() => {
                      if (item.id === tab) return;
                      navigate(item.to);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="so-tableWrap so-tableWrap--staking">
              <table className="so-table so-table--rowLines">
                <thead>
                  <tr>
                    <th scope="col">Trade</th>
                    <th scope="col">Date</th>
                    <th scope="col">Pair</th>
                    <th scope="col">You pay</th>
                    <th scope="col">You get</th>
                    <th scope="col">Status</th>
                    <th scope="col">View</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="so-table__emptyWrap">
                        {tab === "active" ? "Loading active requests…" : "Loading OTC history…"}
                      </td>
                    </tr>
                  ) : pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="so-table__emptyWrap">
                        {empty}
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((row) => {
                      const id = row._id || row.id || row.number;
                      return (
                        <tr key={id}>
                          <td>{row.number || "—"}</td>
                          <td className="so-num">{requestWhen(row.createdAt || row.updatedAt)}</td>
                          <td>{row.pairSymbol || "—"}</td>
                          <td className="so-num">{row.payAmount} {row.payAsset}</td>
                          <td className="so-num">{row.getAmount} {row.getAsset}</td>
                          <td>
                            <span className={`otc-status-chip ${statusTone(row.status)}`}>
                              {stageLabel(row.status, row)}
                            </span>
                          </td>
                          <td>
                            <button
                              type="button"
                              className="so-stkActionBtn so-stkActionBtn--view"
                              title="View details"
                              onClick={() => setSelectedId(id)}
                            >
                              <i className="ri-eye-line" aria-hidden="true" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <OrderTablePagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              label="OTC orders pages"
            />
            {selected ? <TradeDetail row={selected} onClose={() => setSelectedId("")} /> : null}
          </main>
        </div>
      </div>
    </div>
  );
};

export default OtcOrders;
